import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Trash2, Lock, UploadCloud, PartyPopper, Megaphone, Sparkles, CheckCircle2, Loader2, Plus } from "lucide-react";
import { Logo } from "./Logo";
import SiteTexts from "./SiteTexts";
import AdminEstoque from "./admin/AdminEstoque";
import AdminVendas from "./admin/AdminVendas";
import AdminTravas from "./admin/AdminTravas";
import AdminClientes from "./admin/AdminClientes";
import AdminCupons from "./admin/AdminCupons";
import { API_BASE, BACKEND_URL } from "../lib/site";

const TYPES = [
    { value: "inauguracao", label: "Inauguração" },
    { value: "promocao", label: "Promoção" },
    { value: "evento", label: "Evento" },
];

const formatError = (detail) => {
    if (detail == null) return "Algo deu errado. Tente novamente.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    return String(detail);
};

const inputCls =
    "w-full rounded-xl border-2 border-[#1A231E]/12 bg-white px-4 py-3 text-base font-semibold text-[#1A231E] placeholder:text-[#1A231E]/35 placeholder:font-normal focus:outline-none focus:border-[#F57A3A] transition-colors";

export default function Admin() {
    const [user, setUser] = useState(undefined);
    const [loginError, setLoginError] = useState("");
    const [posts, setPosts] = useState([]);
    const [msg, setMsg] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({ type: "inauguracao", title: "", description: "", event_date: "", image_path: "", image_url: "" });

    const loadPosts = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/posts`);
            setPosts(data);
        } catch {
            setPosts([]);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
                setUser(data);
                loadPosts();
            } catch {
                try {
                    await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
                    const { data } = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
                    setUser(data);
                    loadPosts();
                } catch {
                    setUser(null);
                }
            }
        })();
    }, [loadPosts]);

    const login = async (e) => {
        e.preventDefault();
        setLoginError("");
        const email = e.target.email.value;
        const password = e.target.password.value;
        try {
            const { data } = await axios.post(`${API_BASE}/auth/login`, { email, password }, { withCredentials: true });
            setUser(data);
            loadPosts();
        } catch (err) {
            setLoginError(formatError(err.response?.data?.detail) || err.message);
        }
    };

    const uploadImage = async (file) => {
        setUploading(true);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(`${API_BASE}/admin/upload`, fd, { withCredentials: true });
            setForm((f) => ({ ...f, image_path: data.path, image_url: `${BACKEND_URL}${data.url}` }));
            setMsg({ ok: true, text: "Foto carregada!" });
        } catch (err) {
            setMsg({ ok: false, text: formatError(err.response?.data?.detail) || err.message });
        } finally {
            setUploading(false);
        }
    };

    const createPost = async (e) => {
        e.preventDefault();
        setMsg(null);
        try {
            await axios.post(
                `${API_BASE}/posts`,
                {
                    type: form.type,
                    title: form.title,
                    description: form.description,
                    event_date: form.event_date || null,
                    image_path: form.image_path || null,
                },
                { withCredentials: true }
            );
            setForm({ type: form.type, title: "", description: "", event_date: "", image_path: "", image_url: "" });
            setMsg({ ok: true, text: "Publicado no site!" });
            loadPosts();
        } catch (err) {
            setMsg({ ok: false, text: formatError(err.response?.data?.detail) || err.message });
        }
    };

    const removePost = async (id) => {
        try {
            await axios.delete(`${API_BASE}/posts/${id}`, { withCredentials: true });
            setPosts((p) => p.filter((x) => x.id !== id));
        } catch (err) {
            setMsg({ ok: false, text: formatError(err.response?.data?.detail) || err.message });
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true });
        } finally {
            setUser(null);
        }
    };

    useEffect(() => {
        document.title = "Conbox — Administração";
    }, []);

    if (user === undefined) {
        return (
            <div className="min-h-screen bg-[#073A22] grid place-items-center text-[#F7F4EC]">
                <Loader2 className="w-8 h-8 animate-spin text-[#F1C75B]" />
            </div>
        );
    }

    if (user === null) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#0E6B3F] to-[#073A22] grid place-items-center px-4 py-16">
                <div className="w-full max-w-md">
                    <div className="flex justify-center mb-6">
                        <Logo light />
                    </div>
                    <form
                        onSubmit={login}
                        data-testid="admin-login-form"
                        className="rounded-[2rem] bg-[#F7F4EC] p-8 sticker -rotate-1"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <Lock className="w-6 h-6 text-[#0E6B3F]" />
                            <h1 className="text-2xl font-extrabold text-[#1A231E]">Área do administrador</h1>
                        </div>
                        <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="admin-email">
                            E-mail
                        </label>
                        <input id="admin-email" data-testid="admin-email-input" name="email" type="email" required placeholder="admin@conbox.com.br" className={inputCls} />
                        <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5 mt-4" htmlFor="admin-password">
                            Senha
                        </label>
                        <input id="admin-password" data-testid="admin-password-input" name="password" type="password" required placeholder="••••••••" className={inputCls} />
                        <button
                            type="submit"
                            data-testid="admin-login-button"
                            className="mt-6 w-full rounded-full bg-[#F57A3A] hover:bg-[#E06324] text-[#F7F4EC] font-extrabold py-4 transition-all duration-200 hover:-translate-y-0.5"
                        >
                            Entrar
                        </button>
                        {loginError && (
                            <p data-testid="admin-login-error" className="mt-4 rounded-xl bg-red-50 border-2 border-red-200 px-4 py-3 text-sm font-semibold text-red-600">
                                {loginError}
                            </p>
                        )}
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F7F4EC] text-[#1A231E]" data-testid="admin-panel">
            <header className="bg-[#073A22] py-4 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40">
                <Logo light />
                <button
                    onClick={logout}
                    data-testid="admin-logout-button"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-[#F7F4EC]/30 text-[#F7F4EC] font-bold px-5 py-2.5 text-sm hover:border-[#F1C75B] hover:text-[#F1C75B] transition-colors"
                >
                    <LogOut className="w-4 h-4" />
                    Sair
                </button>
            </header>

            <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
                <h1 className="text-3xl font-extrabold tracking-tight">Painel do administrador</h1>
                <p className="mt-2 text-[#1A231E]/70">
                    Gerencie novidades, textos, estoque, vendas, travas, clientes e cupons.
                </p>
                <div className="mt-5 flex flex-wrap gap-2" data-testid="admin-sections-nav">
                    {[
                        ["#admin-novidades", "Novidades"],
                        ["#admin-estoque", "Estoque"],
                        ["#admin-vendas", "Vendas"],
                        ["#admin-travas", "Travas"],
                        ["#admin-clientes", "Clientes"],
                        ["#admin-cupons", "Cupons"],
                        ["#admin-textos", "Textos do site"],
                    ].map(([href, label]) => (
                        <button
                            key={href}
                            data-testid={`admin-nav-${href.slice(1)}`}
                            onClick={() => document.getElementById(href.slice(1))?.scrollIntoView({ behavior: "smooth" })}
                            className="rounded-full border-2 border-[#0E6B3F] text-[#0E6B3F] text-xs font-extrabold uppercase tracking-wider px-4 py-2 hover:bg-[#0E6B3F] hover:text-[#F7F4EC] transition-colors"
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div id="admin-novidades" className="scroll-mt-28 mt-8">
                <h2 className="text-xl font-extrabold tracking-tight">Gerenciar novidades</h2>
                <p className="mt-2 text-[#1A231E]/70">
                    Publique inaugurações, promoções e eventos — aparecem na seção "Novidades" do site.
                </p>

                <div className="mt-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
                    <form onSubmit={createPost} data-testid="admin-post-form" className="rounded-[1.75rem] bg-white border-2 border-[#1A231E]/12 sticker-soft p-7">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="admin-post-type">
                                    Tipo *
                                </label>
                                <select
                                    id="admin-post-type"
                                    data-testid="admin-post-type-select"
                                    value={form.type}
                                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                                    className={inputCls}
                                >
                                    {TYPES.map((t) => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="admin-post-date">
                                    Data (opcional)
                                </label>
                                <input
                                    id="admin-post-date"
                                    data-testid="admin-post-date-input"
                                    type="date"
                                    value={form.event_date}
                                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="admin-post-title">
                                    Título *
                                </label>
                                <input
                                    id="admin-post-title"
                                    data-testid="admin-post-title-input"
                                    required
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder="Ex.: Inauguração no Residencial Jardins do Sol"
                                    className={inputCls}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="admin-post-desc">
                                    Descrição *
                                </label>
                                <textarea
                                    id="admin-post-desc"
                                    data-testid="admin-post-desc-input"
                                    required
                                    rows={4}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder="Conte como foi a inauguração, os detalhes da promoção ou do evento..."
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="admin-post-image">
                                    Foto (opcional)
                                </label>
                                <div className="flex items-center gap-4">
                                    <label
                                        htmlFor="admin-post-image"
                                        data-testid="admin-post-image-input"
                                        className="inline-flex items-center gap-2 rounded-full border-2 border-[#0E6B3F] text-[#0E6B3F] font-bold px-5 py-2.5 text-sm cursor-pointer hover:bg-[#0E6B3F]/5 transition-colors"
                                    >
                                        <UploadCloud className="w-4 h-4" />
                                        {uploading ? "Enviando..." : "Escolher foto"}
                                    </label>
                                    <input
                                        id="admin-post-image"
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        className="hidden"
                                        disabled={uploading}
                                        onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                                    />
                                    {form.image_url && (
                                        <img src={form.image_url} alt="Prévia" data-testid="admin-image-preview" className="w-16 h-16 rounded-xl object-cover border-2 border-[#1A231E]/10" />
                                    )}
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="admin-post-submit"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F57A3A] hover:bg-[#E06324] text-[#F7F4EC] font-extrabold py-4 transition-all duration-200 hover:-translate-y-0.5"
                        >
                            <Plus className="w-5 h-5" />
                            Publicar novidade
                        </button>

                        {msg && (
                            <p
                                data-testid="admin-form-message"
                                className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold border-2 ${
                                    msg.ok
                                        ? "bg-green-50 border-green-200 text-green-700"
                                        : "bg-red-50 border-red-200 text-red-600"
                                }`}
                            >
                                {msg.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> : null}
                                {msg.text}
                            </p>
                        )}
                    </form>

                    <div>
                        <h2 className="text-lg font-extrabold uppercase tracking-wider text-[#1A231E]/60">
                            Publicadas ({posts.length})
                        </h2>
                        <div className="mt-4 space-y-3">
                            {posts.length === 0 && (
                                <p className="rounded-xl border-2 border-dashed border-[#1A231E]/15 px-4 py-8 text-center text-sm font-semibold text-[#1A231E]/50">
                                    Nenhuma novidade publicada ainda.
                                </p>
                            )}
                            {posts.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid="admin-post-item"
                                    className="flex items-center gap-4 rounded-2xl bg-white border-2 border-[#1A231E]/10 p-3.5"
                                >
                                    {p.image_path ? (
                                        <img src={`${BACKEND_URL}/api/media/${p.image_path}`} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                                    ) : (
                                        <div className="w-14 h-14 rounded-xl bg-[#EFEAE0] grid place-items-center shrink-0">
                                            {p.type === "inauguracao" ? <PartyPopper className="w-5 h-5 text-[#F57A3A]" /> : p.type === "promocao" ? <Megaphone className="w-5 h-5 text-[#F1C75B]" /> : <Sparkles className="w-5 h-5 text-[#0E6B3F]" />}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-extrabold truncate">{p.title}</p>
                                        <p className="text-xs font-bold uppercase tracking-wider text-[#1A231E]/45">
                                            {TYPES.find((t) => t.value === p.type)?.label || p.type}
                                            {p.event_date ? ` · ${new Date(`${p.event_date}T12:00:00`).toLocaleDateString("pt-BR")}` : ""}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => removePost(p.id)}
                                        data-testid="admin-post-delete"
                                        aria-label={`Excluir ${p.title}`}
                                        className="w-10 h-10 rounded-xl grid place-items-center text-red-500 hover:bg-red-50 transition-colors shrink-0"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                </div>

                <hr className="my-12 border-t-2 border-[#1A231E]/10" />

                <AdminEstoque />
                <hr className="my-12 border-t-2 border-[#1A231E]/10" />
                <AdminVendas />
                <hr className="my-12 border-t-2 border-[#1A231E]/10" />
                <AdminTravas />
                <hr className="my-12 border-t-2 border-[#1A231E]/10" />
                <AdminClientes />
                <hr className="my-12 border-t-2 border-[#1A231E]/10" />
                <AdminCupons />

                <hr className="my-12 border-t-2 border-[#1A231E]/10" />

                <div id="admin-textos" className="scroll-mt-28">
                    <SiteTexts />
                </div>
            </main>
        </div>
    );
}
